<template>
  <div class="class-1">
    <!-- Header -->
    <AppHeader title="Customers Management">
    </AppHeader>

    <!-- Search and Add New Customer Button -->
    <div class="search-add-container">
      <input 
        type="text" 
        v-model="searchQuery" 
        placeholder="Search customers..." 
        class="search-input"
      />
      <button class="add-customer-btn" @click="navigateToAddCustomer">Add New Customer</button>
    </div>

    <!-- Table Structure -->
    <div class="table-container">
      <table id="customerTable">
        <thead>
          <tr>
            <th>Name</th>
            <th>Phone</th>
            <th>Address</th>
            <th>Registration Date</th>
            <th>Status</th>
            <th>Operations</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(customer, index) in paginatedCustomers" :key="index">
            <td>{{ customer.name }}</td>
            <td>{{ customer.phone }}</td>
            <td>{{ customer.area + ' - ' +  customer.street + ' - ' + customer.build + ' - ' + customer.flat + ' - ' + customer.floor}}</td>
            <td>{{ formatDate(customer.registration_date) }}</td>
            <td :class="customer.isSuspend ? '' : ''">
              {{ customer.isSuspend ? 'Suspended' : 'Active' }}
            </td>
            <td class="action-buttons">
              <button data-tooltip="Edit" @click="handleEdit(customer)">
                <img src="@/assets/edit_icon.png" alt="Edit">
              </button>
              <button 
                data-tooltip="Delete" 
                @click="handleDelete(customer)"
                class="delete-btn"
              >
                <img src="@/assets/delete_icon.png" alt="Delete">
              </button>
              <button 
                :data-tooltip="customer.isSuspend ? 'Activate' : 'Suspend'" 
                @click="handleToggleStatus(customer)"
                :class="customer.isSuspend ? 'activate-btn' : 'suspend-btn'"
              >
                <img 
                  :src="customer.isSuspend 
                    ? require('@/assets/suspend_icon.png') 
                    : require('@/assets/activate_icon.png')" 
                  :alt="customer.isSuspend ? 'Activate' : 'Suspend'"
                >
              </button>
               <button 
  :data-tooltip="customer.credit ? 'Disable Credit' : 'Enable Credit'" 
  @click="handleToggleCredit(customer)"
  :class="customer.credit ? 'credit-active-btn' : 'credit-inactive-btn'"
>
  <i class="fas fa-credit-card"></i>
</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination-container" v-if="totalPages > 1">
      <button 
        @click="prevPage" 
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        Previous
      </button>
      
      <span class="page-info">
        Page {{ currentPage }} of {{ totalPages }}
      </span>
      
      <button 
        @click="nextPage" 
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        Next
      </button>
      
      <select v-model="itemsPerPage" @change="resetPagination" class="page-size-select">
        <option value="5">5 per page</option>
        <option value="10">10 per page</option>
        <option value="20">20 per page</option>
        <option value="40">40 per page</option>
      </select>
      <button class="back-btn" @click="goBack">
        <i class="fas fa-arrow-left"></i> Back
      </button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      customers: [],
      searchQuery: '',
      currentDate: "",
      currentTime: "",
      currentPage: 1,
      itemsPerPage: 10,
      showBackButton: true
    };
  },
  computed: {
    filteredCustomers() {
      const query = this.searchQuery.toLowerCase();
      if (!query) return this.customers;
      
      return this.customers.filter(customer => {
        if (customer.name.toLowerCase().includes(query)) return true;
        if (customer.phone.toLowerCase().includes(query)) return true;
        
        const addressComponents = [
          customer.area,
          customer.street,
          customer.build,
          customer.flat,
          customer.floor
        ].join(' ').toLowerCase();
        if (addressComponents.includes(query)) return true;
        
        if (this.formatDate(customer.registration_date).toLowerCase().includes(query)) return true;
        
        const status = customer.isSuspend ? 'suspended' : 'active';
        if (status.includes(query)) return true;
        
        return false;
      });
    },
    paginatedCustomers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCustomers.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredCustomers.length / this.itemsPerPage);
    }
  },
  mounted() {
    this.updateDateTime(); 
    setInterval(this.updateDateTime, 1000); 
    this.fetchCustomers();
    this.checkBackButton();
  },
  methods: {
    checkBackButton() {
      this.showBackButton = window.history.length > 1;
    },
    goBack() {
      this.$router.go(-1);
    },
    async handleToggleCredit(customer) {
  try {
    if (!this.$hasPermission('edit customers')) {
      this.$toast.error('Not Permitted');
      return;
    }
    
    this.$root.isLoading = true;
    const token = localStorage.getItem('authToken');
    const newCreditStatus = !customer.credit;
    
    const response = await fetch(`${this.$BASE_API_URL}/api/customers/toggle_credit/${customer.id}`, {
      method: 'Put',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });

    if (!response.ok) {
      throw new Error('Failed to update credit status');
    }

    const result = await response.json();
    if (result.status) {
      customer.credit = newCreditStatus;
      this.$toast.success(result.message || 'Credit status updated successfully');
    } else {
      throw new Error(result.message || 'Failed to update credit status');
    }
  } catch (error) {
    console.error('Error updating credit status:', error);
    this.$toast.error(error.message || 'Failed to update credit status');
  } finally {
    this.$root.isLoading = false;
  }
},
    updateDateTime() {
      const now = new Date();
      this.currentDate = now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      this.currentTime = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    async fetchCustomers() {
      try {
        this.$root.isLoading = true; 
        const token = localStorage.getItem('authToken'); 
        const response = await fetch(`${this.$BASE_API_URL}/api/customers`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch customers');
        }

        const { data } = await response.json();
        this.customers = data;
      } catch (error) {
        console.error('Error fetching customers:', error);
        this.$toast.error('Failed to load customer data');
      } finally {
        this.$root.isLoading = false;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    navigateToAddCustomer() {
      if (!this.$hasPermission('create customers')){
        this.$toast.error('Not Permitted');
        return;
      }
      this.$router.push({ name: "AddCustomerComponent" });
    },
    handleEdit(customer) {
      if (!this.$hasPermission('edit customers')){
        this.$toast.error('Not Permitted');
        return;
      }
      this.$router.push({ 
        name: "EditCustomerComponent", 
        params: { id: customer.id } 
      });
    },
    async handleDelete(customer) {
      if (!this.$hasPermission('delete customers')){
        this.$toast.error('Not Permitted');
        return;
      }
      if (!confirm(`Are you sure you want to delete ${customer.name}?`)) {
        return;
      }
      
      try {
        this.$root.isLoading = true; 
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${this.$BASE_API_URL}/api/customers/${customer.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete customer');
        }

        this.customers = this.customers.filter(c => c.id !== customer.id);
        this.$toast.success('Customer deleted successfully');
        this.resetPagination();
      } catch (error) {
        console.error('Error deleting customer:', error);
        this.$toast.error('Failed to delete customer');
      } finally {
        this.$root.isLoading = false;
      }
    },
    async handleToggleStatus(customer) {
      try {
        if (!this.$hasPermission('suspend customers') && !this.$hasPermission('activate customers')){
          this.$toast.error('Not Permitted');
          return;
        }
        this.$root.isLoading = true; 
        const token = localStorage.getItem('authToken');
        const endpoint = `/api/customers/set_suspend/${customer.id}`;
        const newStatus = !customer.isSuspend;  
        
        const response = await fetch(`${this.$BASE_API_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                isSuspend: newStatus  
            })
        });

        if (!response.ok) {
            throw new Error('Failed to update customer status');
        }

        customer.isSuspend = newStatus;
        this.$forceUpdate();
      } catch (error) {
          console.error('Error updating customer status:', error);
          this.$toast.error('Failed to update customer status');
      } finally {
        this.$root.isLoading = false;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    resetPagination() {
      this.currentPage = 1;
    }
  },
  watch: {
    searchQuery() {
      this.resetPagination();
    }
  }
};
</script>

<style scoped>
th, td {
  border: 1px solid;
  padding: 8px;
  text-align: left;
}

.search-add-container {
  display: flex;
  justify-content: space-between;
  margin: 10px;
}

.search-input {
  padding: 10px;
  width: 300px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
  color: #000;
}

.add-customer-btn {
  padding: 10px;
  background-color: #4caf50;
  color: white;
  border: none;
  cursor: pointer;
  width: max-content;
  border-radius: 5px;
  margin-top: 0;
}

.back-btn {
  padding: 8px 12px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
}

.back-btn:hover {
  background-color: #5a6268;
}

.table-container {
  overflow-x: auto;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 10px;
}

.pagination-btn {
  padding: 8px 16px;
  background-color: #4caf50;
  color: var(--font-color);
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.pagination-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.page-info {
  margin: 0 10px;
  color: var(--font-color);
}

.page-size-select {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin-left: 10px;
}

.text-success {
  color: #28a745;
}
.delete-btn img {
  filter: invert(20%) sepia(100%) saturate(500%) hue-rotate(300deg);
}

.suspend-btn {
  color: #dc3545;
}

.activate-btn {
  color: #28a745;
}

.action-buttons button[data-tooltip="Suspend"] img {
  filter: none; 
}
</style>